import React, { useState } from 'react';
import { usePriceToIncomeRatioData } from '@/hooks/usePriceToIncomeRatioData';
import { usePeerPriceToIncomeRatioData } from '@/hooks/usePeerPriceToIncomeRatioData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shared/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/shared/tooltip";
import { formatPlaceName, cn } from '@/lib/utils';
import { ComparisonBarChart } from './ComparisonBarChart';
import { Place } from '@/types';

interface Props {
  place: string | null;
  placeFips: string | null;
  peers: Place[];
  labelFontSize?: number;
}

const PriceToIncomeRatioCard: React.FC<Props> = ({ place, placeFips, peers, labelFontSize }) => {
  const { data: placeData, loading, error } = usePriceToIncomeRatioData(place, placeFips);
  const { data: peerData, loading: peersLoading } = usePeerPriceToIncomeRatioData(peers);
  const [isTitleHovered, setIsTitleHovered] = useState(false);

  const getRankSuffix = (rank: number) => {
    if (rank > 10 && rank < 20) return 'th';
    const lastDigit = rank % 10;
    switch (lastDigit) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const chartData = React.useMemo(() => {
    if (!placeData.length || !peerData) return [];
    
    const mostRecentPlaceData = placeData[placeData.length - 1];

    const allCities = peers
      .filter(peer => peer.place_fips.toString() !== placeFips)
      .map(peer => {
        const peerRecords = peerData[peer.place_fips];
        const latestPeerRecord = peerRecords ? peerRecords[0] : null;
        return {
          name: peer.name,
          value: parseFloat((latestPeerRecord?.price_to_income ?? 0).toFixed(2)),
          isSelected: false,
        };
      });

    if (mostRecentPlaceData) {
      allCities.push({
        name: mostRecentPlaceData.Place,
        value: parseFloat((mostRecentPlaceData.price_to_income ?? 0).toFixed(2)),
        isSelected: true,
      });
    }

    return allCities.sort((a, b) => a.value - b.value); // Sort ascending, lower ratio is better
  }, [placeData, peerData, peers, placeFips]);

  const calculateRank = () => {
    if (!place) return null;
    const rank = chartData.findIndex(item => item.isSelected) + 1;
    return rank > 0 ? `${rank}${getRankSuffix(rank)}` : null;
  };
  
  const lastYear = placeData.length > 0 ? placeData[placeData.length - 1]?.year : null;

  if (!place || !placeFips) {
    return (
      <Card className="p-4 border border-[#f573a0]">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Price to Income Ratio</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Please select a place to view home price to income ratio data.</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="flex flex-col p-4 border border-[#f573a0]">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Price to Income Ratio for {formatPlaceName(place)}</CardTitle>
        </CardHeader>
        <CardContent>
          <div>Loading data...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="flex flex-col p-4 border border-[#f573a0]">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Price to Income Ratio for {formatPlaceName(place)}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500">Error: {error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card className="flex flex-col p-4 border border-[#ca0270] relative w-full max-w-[600px]">
        <div className="absolute top-4 left-4 bg-[#908ddb] text-white text-sm font-semibold px-3 py-1 rounded-full">
          Affordability
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute top-4 right-4 bg-[#ca0270] text-white text-sm font-semibold px-3 py-1 rounded-full cursor-help">
              {peersLoading ? 'Calculating...' : `Peer Ranking: ${calculateRank() || 'N/A'}`}
            </div>
          </TooltipTrigger>
          <TooltipContent className="opacity-100 z-50 bg-white text-black" sideOffset={8}>
            <p>Ranked by: Lowest Price-to-Income Ratio</p>
          </TooltipContent>
        </Tooltip>
        <CardHeader className="pt-8 pb-0">
          <CardTitle
            className="text-xl lg:text-2xl font-bold"
          >
            Price to Income Ratio
          </CardTitle>
          <p className="text-sm text-gray-500 min-h-[4rem] pt-1">Ratio of median home value to median household income for {lastYear}</p>
        </CardHeader>
        <CardContent className="relative p-0">
          <ComparisonBarChart
            data={chartData}
            yAxisLabel="Price-to-Income Ratio"
            tooltipFormatter={(value) => `${value.toFixed(2)}`}
            selectedColor="#f6b90d"
            height={200}
            labelFontSize={labelFontSize}
          />
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default PriceToIncomeRatioCard; 